import type { ReactNode } from 'react';
import { useState } from 'react';
import type { MusicAlbum, MusicArtist } from '../api.ts';
import { MusicIcon } from '../Icon.tsx';
import { mediaUrl } from '../mediaToken.ts';

function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  return hours > 0 ? `${hours} sa ${minutes} dk` : `${minutes} dk`;
}

function Cover({
  itemId,
  thumbnailUrl,
  alt,
}: {
  itemId: string | null;
  thumbnailUrl?: string | null | undefined;
  alt: string;
}): ReactNode {
  const [failed, setFailed] = useState(false);
  if ((itemId === null && !thumbnailUrl) || failed) {
    return (
      <span className="music-cover music-cover--fallback" aria-hidden="true">
        <MusicIcon size={30} />
      </span>
    );
  }
  return (
    <img
      className="music-cover"
      src={
        thumbnailUrl ??
        (itemId === null ? '' : mediaUrl(`/api/items/${encodeURIComponent(itemId)}/thumbnail`))
      }
      alt={alt}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

export function AlbumCard({ album, onOpen }: { album: MusicAlbum; onOpen: () => void }): ReactNode {
  return (
    <button type="button" className="music-album-card" onClick={onOpen}>
      <span className="music-album-card__cover">
        <Cover
          itemId={album.coverItemId}
          thumbnailUrl={album.coverThumbnailUrl}
          alt={`${album.title} kapağı`}
        />
      </span>
      <strong>{album.title}</strong>
      <span>{album.artist}</span>
      <small>
        {album.year === null ? '' : `${album.year} · `}
        {album.trackCount} parça
        {album.durationMs > 0 ? ` · ${formatDuration(album.durationMs)}` : ''}
      </small>
    </button>
  );
}

export function ArtistCard({
  artist,
  onOpen,
}: {
  artist: MusicArtist;
  onOpen: () => void;
}): ReactNode {
  return (
    <button type="button" className="music-artist-card" onClick={onOpen}>
      <Cover
        itemId={artist.coverItemId}
        thumbnailUrl={artist.coverThumbnailUrl}
        alt={`${artist.name} görseli`}
      />
      <span>
        <strong>{artist.name}</strong>
        <small>
          {artist.albumCount} albüm · {artist.trackCount} parça
        </small>
      </span>
    </button>
  );
}

export { Cover, formatDuration };
